import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { createPath, Paths } from '../../../../app/router/app.router';
import { PermissionBoundary } from '../../../../core/permissions/permission-boundary.component';
import { LoadedActionsButtonItem } from '../../../../ui/components/pure/actions-button/actions-button.types';
import { DeliveryCard } from '../../../../ui/components/pure/delivery-card/delivery-card.component';
import { HoverCardWithCallToAction } from '../../../../ui/components/pure/hover-card-with-call-to-action/hover-card-with-call-to-action.component';
import { Unknown } from '../../../../ui/components/pure/icons/Unknown.icon';
import { StatusBadge } from '../../../../ui/components/pure/status-badge/status-badge.component';
import { Delivery } from '../../../deliveries/models/delivery.model';
import { useQueryListDeliveryContainerContentHistory } from '../../hubs.queries';
import { useQueryListDeliveryContainerContents } from '../../hubs.queries';
import {
  getDeliveryStateLabelForProject,
  transformTimelineTextForProject
} from '../../../../core/util/status-label.util';
import { DeliveryContainerContentsModel } from '../../models/delivery-container-contents.model';
import { DeliveryContainerHistoricalContentsModel } from '../../models/delivery-container-historical-content.model';
import { DeliveryContainer } from '../../models/delivery-container.model';
import { useBookingFlowType } from '../../../orders/list/actions/book-order/use-booking-flow-type.hook';
import { BookingFlowType } from '../../../../ui/enums/booking-flow-type.enum';

export function DeliveryContainerHoverCard({
  deliveryContainer,
  actions
}: {
  deliveryContainer: DeliveryContainer;
  actions: LoadedActionsButtonItem[];
}) {
  return deliveryContainer.isKiosk ? (
    <WithKiosk deliveryContainer={deliveryContainer} actions={actions} />
  ) : (
    <WithDeliveryContainer deliveryContainer={deliveryContainer} actions={actions} />
  );
}

function WithDeliveryContainer({
  deliveryContainer,
  actions
}: {
  deliveryContainer: DeliveryContainer;
  actions: LoadedActionsButtonItem[];
}) {
  const contentsQueryResult = useQueryListDeliveryContainerContents(deliveryContainer.id);
  const contentHistory = useQueryListDeliveryContainerContentHistory(deliveryContainer.id, { perPageRecords: 3 });

  const isLoading = contentsQueryResult.isLoading || contentHistory.isLoading;
  const errorMessage = contentsQueryResult.error || contentHistory.error ? 'Something went wrong' : undefined;

  const contents = useMemo(() => {
    if (contentsQueryResult.data?.length) {
      return contentsQueryResult.data;
    }
    if (contentHistory.data?.items) {
      return contentHistory.data.items;
    }
    return [];
  }, [contentsQueryResult.data, contentHistory.data?.items]);

  const cardTitle = makeCardTitle(deliveryContainer);
  // Find project ID from either current contents or historical contents
  const projectId =
    contentsQueryResult.data?.find((content) => content.delivery?.project?.id)?.delivery?.project.id ||
    contentHistory.data?.items?.find((content) => content.delivery?.project?.id)?.delivery?.project.id;

  const bookingFlowType = useBookingFlowType(projectId || '');

  const deliveryCardProps = useMemo(() => {
    return makeDeliveryCardProps(contents, bookingFlowType);
  }, [contents, bookingFlowType]);

  const contentsTitle = makeContentsTitle(
    contentsQueryResult.data?.length,
    contentHistory.data?.items.length,
    bookingFlowType
  );
  const noContentsMessage = deliveryCardProps.length === 0 ? 'No upcoming, current or recent contents' : undefined;

  return (
    <DeliveryContainerCard
      key={deliveryContainer.id}
      cardTitle={cardTitle}
      isLoading={isLoading}
      errorMessage={errorMessage}
      contentsTitle={contentsTitle}
      noContentsMessage={noContentsMessage}
      deliveryCardProps={deliveryCardProps}
      actions={actions}
    />
  );
}

// Exported for Storybook
export function DeliveryContainerCard({
  cardTitle,
  isLoading,
  errorMessage,
  contentsTitle,
  noContentsMessage,
  deliveryCardProps,
  actions
}: {
  cardTitle: string;
  isLoading: boolean;
  errorMessage?: string;
  contentsTitle: string;
  noContentsMessage?: string;
  deliveryCardProps: ReturnType<typeof makeDeliveryCardProps>;
  actions: LoadedActionsButtonItem[];
}) {
  return (
    <HoverCardWithCallToAction
      cardTitle={cardTitle}
      isLoading={isLoading}
      errorMessage={errorMessage}
      contentsTitle={contentsTitle}
      noContentsMessage={noContentsMessage}
      actions={actions}
    >
      <Grid
        container
        sx={{ flexDirection: 'column' }}
        spacing={1}
        wrap="nowrap"
        style={{ maxHeight: '265px', overflowY: 'auto' }}
      >
        {deliveryCardProps.map((props) => (
          <Grid key={props.link}>
            <DeliveryCard {...props} key={props.link} />
          </Grid>
        ))}
      </Grid>
    </HoverCardWithCallToAction>
  );
}

function WithKiosk({
  deliveryContainer,
  actions
}: {
  deliveryContainer: DeliveryContainer;
  actions: LoadedActionsButtonItem[];
}) {
  return (
    <PermissionBoundary availableUiFeatures={['internal-groundfloor']}>
      <HoverCardWithCallToAction cardTitle={`#${deliveryContainer.name} - Kiosk`} isLoading={false} actions={actions} />
    </PermissionBoundary>
  );
}

function makeCardTitle(deliveryContainer: DeliveryContainer) {
  return `#${deliveryContainer.name} - ${deliveryContainer.availabilityDescription}`;
}

function makeContentsTitle(contentsCount?: number, contentHistoryCount?: number, bookingFlowType?: BookingFlowType) {
  if (contentsCount === 1) {
    return bookingFlowType
      ? transformTimelineTextForProject('Current Delivery', bookingFlowType) || 'Current Delivery'
      : 'Current Delivery';
  } else if (contentsCount && contentsCount > 1) {
    return bookingFlowType
      ? transformTimelineTextForProject('Current Deliveries', bookingFlowType) || 'Current Deliveries'
      : 'Current Deliveries';
  } else if (contentHistoryCount && contentHistoryCount > 0) {
    return bookingFlowType
      ? transformTimelineTextForProject('Recent Deliveries', bookingFlowType) || 'Recent Deliveries'
      : 'Recent Deliveries';
  }
  return '';
}

// Make the DeliveryCard props from the Deliveries array
function makeDeliveryCardProps(
  contents: (DeliveryContainerContentsModel | DeliveryContainerHistoricalContentsModel)[],
  bookingFlowType: BookingFlowType
) {
  return contents.map((content) => {
    if (content.delivery) {
      const stateLabel = getDeliveryStateLabelForProject(content.delivery.state, bookingFlowType);
      const stateColor = content.delivery.stateColor;

      let link = '';
      if (content.delivery.isRetailWithOrder()) {
        link = createPath(Paths.order, {
          projectID: content.delivery.project.id,
          orderID: content.delivery.workflow.id
        });
      } else {
        link = createPath(Paths.delivery, { projectID: content.delivery.project.id, deliveryID: content.delivery.id });
      }

      return {
        title: content.delivery.deliveredTo?.party.name || 'Unknown Recipient',
        Icon: content.delivery.deliveryTypeIcon,
        StatusBadge() {
          return <StatusBadge label={stateLabel} color={stateColor} cornerRounding="med" />;
        },
        image: content.delivery.deliveredBy?.party?.imageURL,
        imageFallback: content.delivery.deliveredBy?.party?.name,
        withBorder: true,
        descriptions: makeDeliveryDescriptions(content.delivery, bookingFlowType),
        link,
        isMasked: false
      };
    } else {
      return {
        isMasked: true,
        withBorder: true,
        descriptions: [
          content.maskedDelivery?.deliveryReference,
          'You do not have permission to interact with this delivery.'
        ].filter((it): it is string => !!it),
        Icon: Unknown
      };
    }
  });
}

function makeDeliveryDescriptions(delivery: Delivery, bookingFlowType: BookingFlowType) {
  const descriptions: string[] = [];
  if (delivery.deliveryReference) {
    descriptions.push(delivery.deliveryReference);
  }
  if (delivery.collectedTime) {
    descriptions.push(`Collected ${dayjs(delivery.collectedTime).calendar()}`);
  } else if (delivery.retrievedTime) {
    descriptions.push(`Retrieved ${dayjs(delivery.retrievedTime).calendar()}`);
  } else if (delivery.deliveredTime) {
    const deliveredText = `Delivered ${dayjs(delivery.deliveredTime).calendar()}`;
    const transformedText = transformTimelineTextForProject(deliveredText, bookingFlowType);
    descriptions.push(transformedText || deliveredText);
  }
  return descriptions;
}
